import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { cashScopeKey, type CashMovement } from "./use-branch-cash";

export type ExpenseRecordPayload = {
  workspaceId: string; cashBoxId: string; occurredAt: string; idempotencyKey: string;
  lines: { amount: string; expenseCategoryId?: string; expenseLineItemId?: string; notes?: string }[];
};
export type ExpenseRecordLine = CashMovement & { lineItemName?: string; lineItemNameAr?: string; reversed?: boolean };
export type ExpenseRecord = { id: string; reference: string; total: string; lines: ExpenseRecordLine[]; photos?: { id: string }[]; canReverse?: boolean };
export const validExpenseAmount = (amount: string) => /^\d{1,12}(?:\.\d{1,2})?$/.test(amount) && Number(amount) > 0;
export const expenseCents = (amount: string) => { const negative = amount.startsWith("-"); const [whole, fraction = ""] = amount.replace(/^-/, "").split("."); return (negative ? -1n : 1n) * (BigInt(whole || "0") * 100n + BigInt(fraction.padEnd(2, "0").slice(0, 2))); };
export const formatExpenseCents = (cents: bigint) => { const abs = cents < 0n ? -cents : cents; return `${cents < 0n ? "-" : ""}${abs / 100n}.${String(abs % 100n).padStart(2, "0")}`; };
export const expenseTotal = (amounts: string[]) => formatExpenseCents(amounts.reduce((sum, amount) => sum + (validExpenseAmount(amount) ? expenseCents(amount) : 0n), 0n));
export function expenseBookGroups(rows: CashMovement[]) {
  const groups = new Map<string, CashMovement[]>();
  for (const row of rows) {
    if (!row.expenseRecordId || row.movementType !== "expense") continue;
    const group = groups.get(row.expenseRecordId) || [];
    group.push(row); groups.set(row.expenseRecordId, group);
  }
  return groups;
}
export function createExpenseRecord(payload: ExpenseRecordPayload): Promise<ExpenseRecord> {
  // One immutable request, including every line: apiRequest handles one PIN challenge
  // and retries this entire body. Never post lines individually.
  return apiRequest("POST", "/api/cash/expense-records", payload).then(response => response.json());
}
export function useExpenseRecord(id: string) {
  const { user, currentWorkspace, roles } = useAuth();
  const client = useQueryClient();
  const key = ["branch-cash", "expense-record", ...cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id)), id];
  const query = useQuery<ExpenseRecord>({
    queryKey: key, enabled: !!currentWorkspace && !!user && !!id,
    queryFn: async () => (await apiRequest("GET", `/api/cash/expense-records/${encodeURIComponent(id)}`)).json(),
  });
  return { query, refresh: () => client.invalidateQueries({ queryKey: ["branch-cash"] }) };
}
